import React, { useEffect, useMemo, useState } from "react";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-alpine.css";

import { useVariable } from "@/services/backstage/BackstageProvider";
import { useAuthenticationService } from "@/services/authentication/authentication-service";
import { handleError } from "@/services/gql/config/URQLProvider";
import { useGetSupportedHolidayRegionsQuery } from "@/services/gql/graphql.generated";
import { AdminSettings } from "@/types/dataManagement.types";
import {
  GenericSettingsGrid,
  SettingsGridRow,
} from "./GenericSettingsGrid";
import { OptionItem } from "./DynamicCellRenderer";
import strings from '../tenantSettings.translations';
import { isRowDataDirty } from "./settingsGrid.utils";

const ESS_BEHAVIOUR_KEYS = {
  allowSelfEditEmail: "essBehaviour.allowSelfEditEmail",
  allowSelfEditPhoneNumber: "essBehaviour.allowSelfEditPhoneNumber",
} as const;

const OPTIMISER_SOLVER_KEYS = {
  enabledSolvers: "optimiser.enabledSolvers",
  enableGeneticAlgorithm: "optimiser.enableGeneticAlgorithm",
  enableCpSat: "optimiser.enableCpSat",
  showEvaluation: "optimiser.showEvaluation",
} as const;

type AdminSettingsProperties = {
  [ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]?: boolean;
  [ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enabledSolvers]?: "geneticAlgorithm" | "cpSat" | "both";
  [OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enableCpSat]?: boolean;
  [OPTIMISER_SOLVER_KEYS.showEvaluation]?: boolean;
  [key: string]: unknown;
};

const getOptimiserEnabledSolvers = (
  properties: AdminSettingsProperties
): "geneticAlgorithm" | "cpSat" | "both" => {
  const enabledSolvers = properties[OPTIMISER_SOLVER_KEYS.enabledSolvers];

  if (
    enabledSolvers === "geneticAlgorithm" ||
    enabledSolvers === "cpSat" ||
    enabledSolvers === "both"
  ) {
    return enabledSolvers;
  }

  if (
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]) &&
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])
  ) {
    return "both";
  }

  if (Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])) {
    return "cpSat";
  }

  return "geneticAlgorithm";
};

const parseProperties = (properties?: string | null): AdminSettingsProperties => {
  if (!properties) {
    return {};
  }

  try {
    const parsed = JSON.parse(properties);
    return parsed && typeof parsed === "object" ? parsed as AdminSettingsProperties : {};
  } catch {
    return {};
  }
};

// ---------------------------------------
// Register Ag-Grid Community Modules
// ---------------------------------------
ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------
// Map API response to rowData
// ---------------------------------------
const mapOrganisationSettingsToRowData = (
  data: AdminSettings,
  holidayRegionOptions: OptionItem[]
): SettingsGridRow[] => {
  const properties = parseProperties(data.properties);

  return [
    {
      id: "section-working-time-rules",
      label: strings.admin.sectionWorkingTimeRules,
      description: strings.admin.sectionWorkingTimeRulesDescription,
      isSection: true,
    },
    {
      id: "minMinutesBeforeBreak",
      label: strings.admin.minMinutesBeforeBreakLabel,
      description: strings.admin.minMinutesBeforeBreakDescription,
      value: data.minMinutesBeforeBreak,
      type: "timespan",
      range: {
        start: "01:00",
        end: "03:00",
        step: 15,
        displayHHmm: false,
      },
      isSection: false,
    },
    {
      id: "minMinutesAfterBreak",
      label: strings.admin.minMinutesAfterBreakLabel,
      description: strings.admin.minMinutesAfterBreakDescription,
      value: data.minMinutesAfterBreak,
      type: "timespan",
      range: {
        start: "01:00",
        end: "03:00",
        step: 15,
        displayHHmm: false,
      },
      isSection: false,
    },
    {
      id: "shortestBreak",
      label: strings.admin.shortestBreakLabel,
      description: strings.admin.shortestBreakDescription,
      value: data.shortestBreak,
      type: "timespan",
      range: {
        start: "00:15",
        end: "01:00",
        step: 15,
        displayHHmm: false,
      },
      isSection: false,
    },
    {
      id: "section-ess-behaviour",
      label: strings.admin.sectionEssBehaviour,
      description: strings.admin.sectionEssBehaviourDescription,
      isSection: true,
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditEmail,
      label: strings.admin.allowEssEditEmailLabel,
      description: strings.admin.allowEssEditEmailDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]),
      type: "boolean",
      isSection: false,
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber,
      label: strings.admin.allowEssEditPhoneNumberLabel,
      description: strings.admin.allowEssEditPhoneNumberDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]),
      type: "boolean",
      isSection: false,
    },
    {
      id: "section-holiday-settings",
      label: strings.admin.sectionHolidaySettings,
      description: strings.admin.sectionHolidaySettingsDescription,
      isSection: true,
    },
    {
      id: "defaultHolidayRegionCode",
      label: strings.admin.defaultHolidayRegionCodeLabel,
      description: strings.admin.defaultHolidayRegionCodeDescription,
      value: data.defaultHolidayRegionCode ?? undefined,
      type: "select",
      options: holidayRegionOptions,
      isSection: false,
    },
    {
      id: "section-optimisation-settings",
      label: strings.admin.sectionOptimisationSettings,
      description: strings.admin.sectionOptimisationSettingsDescription,
      isSection: true,
    },
    {
      id: OPTIMISER_SOLVER_KEYS.enabledSolvers,
      label: strings.admin.enabledSolversLabel,
      description: strings.admin.enabledSolversDescription,
      value: getOptimiserEnabledSolvers(properties),
      type: "select",
      options: [
        {
          value: "geneticAlgorithm",
          label: strings.admin.enabledSolversGeneticAlgorithm
        },
        {
          value: "cpSat",
          label: strings.admin.enabledSolversCpSat
        },
        {
          value: "both",
          label: strings.admin.enabledSolversBoth
        }
      ],
      isSection: false,
    },
    {
      id: OPTIMISER_SOLVER_KEYS.showEvaluation,
      label: strings.admin.optimisationEvaluationLabel,
      description: strings.admin.optimisationEvaluationDescription,
      value: Boolean(properties[OPTIMISER_SOLVER_KEYS.showEvaluation]),
      type: "boolean",
      isSection: false,
    },
  ];
};

// ---------------------------------------
// Map back to API model
// ---------------------------------------
const mapFromRowData = (
  snapshot: AdminSettings,
  rowData: SettingsGridRow[]
): AdminSettings => {
  const getValue = (id: string) =>
    rowData.find((r) => r.id === id)?.value as string | undefined;

  const minMinutesBeforeBreak = getValue("minMinutesBeforeBreak");
  const minMinutesAfterBreak = getValue("minMinutesAfterBreak");
  const shortestBreak = getValue("shortestBreak");
  const defaultHolidayRegionCode = getValue("defaultHolidayRegionCode");
  const allowSelfEditEmail = rowData.find((r) => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditEmail)?.value;
  const allowSelfEditPhoneNumber = rowData.find((r) => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber)?.value;
  const enabledSolvers = rowData.find((r) => r.id === OPTIMISER_SOLVER_KEYS.enabledSolvers)?.value;
  const showEvaluation = rowData.find((r) => r.id === OPTIMISER_SOLVER_KEYS.showEvaluation)?.value;

  if (
    minMinutesBeforeBreak === undefined ||
    minMinutesAfterBreak === undefined ||
    shortestBreak === undefined ||
    defaultHolidayRegionCode === undefined ||
    typeof allowSelfEditEmail !== "boolean" ||
    typeof allowSelfEditPhoneNumber !== "boolean" ||
    typeof showEvaluation !== "boolean" ||
    (enabledSolvers !== "geneticAlgorithm" && enabledSolvers !== "cpSat" && enabledSolvers !== "both")
  ) {
    return snapshot;
  }

  const properties = parseProperties(snapshot.properties);
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail] = allowSelfEditEmail;
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber] = allowSelfEditPhoneNumber;
  properties[OPTIMISER_SOLVER_KEYS.enabledSolvers] = enabledSolvers;
  properties[OPTIMISER_SOLVER_KEYS.showEvaluation] = showEvaluation;
  delete properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm];
  delete properties[OPTIMISER_SOLVER_KEYS.enableCpSat];

  return {
    ...snapshot,
    minMinutesBeforeBreak,
    minMinutesAfterBreak,
    shortestBreak,
    defaultHolidayRegionCode,
    properties: JSON.stringify(properties),
  };
};

// ---------------------------------------
// AdminSettingsGrid Component
// ---------------------------------------
export const AdminSettingsGrid: React.FC = () => {
  const [user, getEffectiveTenantId] = useAuthenticationService((s) => [
    s.user,
    s.getEffectiveTenantId,
  ]);
  const [{ data: supportedHolidayRegionsData, error: supportedHolidayRegionsError, fetching: supportedHolidayRegionsFetching }] =
    useGetSupportedHolidayRegionsQuery();
  if (supportedHolidayRegionsError) handleError(supportedHolidayRegionsError);

  const [snapshot, setSnapshot] = useState<AdminSettings>();
  const [rowData, setRowData] = useState<SettingsGridRow[]>([]);
  const [initialRowData, setInitialRowData] = useState<SettingsGridRow[]>([]);

  const apiUrl = useVariable("apiUrl")?.replace("/graphql", "");
  const tenantId = getEffectiveTenantId();
  const holidayRegionOptions = useMemo<OptionItem[]>(
    () =>
      (supportedHolidayRegionsData?.supportedHolidayRegions ?? [])
        .filter((region): region is NonNullable<typeof region> => region != null)
        .map(region => ({
          label: region.displayName,
          value: region.code,
        })),
    [supportedHolidayRegionsData]
  );

  const isDirty = isRowDataDirty(rowData, initialRowData);

  const createRequestInit = () => {
    const token = user?.access_token;
    if (!token || !tenantId) return null;

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "tenant-id": tenantId,
        "Content-Type": "application/json",
      },
    };
  };

  useEffect(() => {
    const requestInit = createRequestInit();
    if (!requestInit) return;

    const load = async () => {
      const res = await fetch(
        apiUrl + "/api/admin/organisation-settings",
        requestInit
      );
      const data = await res.json();
      const mapped = mapOrganisationSettingsToRowData(data, holidayRegionOptions);

      setSnapshot(data);
      setRowData(mapped);
      setInitialRowData(mapped.map(r => ({ ...r })));
    };

    load();
  }, [user, apiUrl, tenantId, holidayRegionOptions]);

  useEffect(() => {
    if (!snapshot || supportedHolidayRegionsFetching) return;

    const mapped = mapOrganisationSettingsToRowData(snapshot, holidayRegionOptions);
    setRowData(mapped);
    setInitialRowData(mapped.map(r => ({ ...r })));
  }, [snapshot, holidayRegionOptions, supportedHolidayRegionsFetching]);

  const handleSaveChanges = async () => {
    const requestInit = createRequestInit();
    if (!requestInit || !snapshot) return;

    const mapped = mapFromRowData(snapshot, rowData);

    await fetch(apiUrl + "/api/admin/organisation-settings", {
      method: "PUT",
      ...requestInit,
      body: JSON.stringify(mapped),
    });

    setSnapshot(mapped);
    setInitialRowData(rowData.map(r => ({ ...r })));
  };

  return (
    <GenericSettingsGrid
      rowData={rowData}
      onRowDataChange={setRowData}
      onSave={handleSaveChanges}
      isDirty={isDirty}
    />
  );
};

export default AdminSettingsGrid;
